import { isValid } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { isWithin24Hours } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/dateUtils'
import {
  normalizeProviderRole,
  ProviderRole,
} from '@lyrahealth-inc/shared-app-logic/src/features/providers/providerRole'
import { LTSessionType, LTSessionTypes } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/constants'
import { CalendarCardAppointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'

export type ProgramAwareFacilitatorRole = ProviderRole
export type ProgramAwareSessionAttendance = 'attended' | 'canceled' | 'missed'
export type ProgramAwareSessionType = 'caregiver' | 'family' | 'group' | 'intake' | 'medication' | 'teen' | 'unknown'

type BaseProgramAwareSessionRow = {
  appointment: CalendarCardAppointment
  attendance?: ProgramAwareSessionAttendance
  dateTimeLabel: string
  isFacilitatedByAnotherProvider: boolean
  isPast: boolean
  isTooLateToReschedule: boolean
  showActionMenu: boolean
}

export type ProgramAwareSessionRow = BaseProgramAwareSessionRow & {
  facilitatorName?: string
  facilitatorRole?: ProgramAwareFacilitatorRole
  ltSessionType?: LTSessionType
  participantNames: string[]
  sessionType: ProgramAwareSessionType
}

type BuildDBTTeensSessionRowsArgs = {
  appointments: CalendarCardAppointment[]
  authProviderId: string
  caregiverFirstName: string
  clientFirstName: string
  isPast: boolean
}

const getParticipantNames = (
  sessionType: ProgramAwareSessionType,
  caregiverFirstName: string,
  clientFirstName: string,
): string[] => {
  if (sessionType === 'caregiver') {
    return [caregiverFirstName]
  }

  if (sessionType === 'family' || sessionType === 'intake' || sessionType === 'medication') {
    return [clientFirstName, caregiverFirstName]
  }

  if (sessionType === 'group' || sessionType === 'unknown') {
    return []
  }

  return [clientFirstName]
}

const getLTSessionType = ({ sessionNumber, sessionType }: CalendarCardAppointment): LTSessionType | undefined =>
  sessionType ?? (sessionNumber === 1 ? LTSessionTypes.DBT_TEENS_INTAKE_SESSION : undefined)

const PROGRAM_AWARE_SESSION_TYPE_BY_SESSION_TYPE: Record<string, ProgramAwareSessionType> = {
  [LTSessionTypes.DBT_TEENS_INTAKE_SESSION]: 'intake',
  [LTSessionTypes.DBT_TEENS_CAREGIVER_SESSION]: 'caregiver',
  [LTSessionTypes.DBT_TEENS_FAMILY_SESSION]: 'family',
  [LTSessionTypes.DBT_TEENS_GROUP_SESSION]: 'group',
  [LTSessionTypes.DBT_TEENS_MEDICATION_INITIAL_SESSION]: 'medication',
  [LTSessionTypes.DBT_TEENS_MEDICATION_FOLLOWUP_SESSION]: 'medication',
  [LTSessionTypes.DBT_TEENS_SESSION]: 'teen',
  [LTSessionTypes.DBT_TEENS_FOLLOWUP_SESSION]: 'teen',
}

const getProgramAwareSessionType = (sessionType?: LTSessionType): ProgramAwareSessionType =>
  (sessionType && PROGRAM_AWARE_SESSION_TYPE_BY_SESSION_TYPE[sessionType]) || 'unknown'

const getAttendance = (
  { appointmentStatus }: CalendarCardAppointment,
  isPast: boolean,
): ProgramAwareSessionAttendance | undefined => {
  if (appointmentStatus === 'canceled' || appointmentStatus === 'missed') {
    return appointmentStatus
  }

  return isPast ? 'attended' : undefined
}

const getFacilitatorName = (appointment: CalendarCardAppointment): string | undefined => {
  if (appointment.provider?.display_name) {
    return appointment.provider.display_name
  }

  const firstName = appointment.provider?.display_first_name || appointment.provider?.first_name
  const fullName = [firstName, appointment.provider?.last_name].filter(Boolean).join(' ')
  return fullName || appointment.avatarDetails?.displayName || appointment.providerFirstName || undefined
}

const getFacilitatorRole = (appointment: CalendarCardAppointment): ProgramAwareFacilitatorRole | undefined =>
  normalizeProviderRole(appointment.programTaxonomy?.role) ?? normalizeProviderRole(appointment.provider?.lyra_type)

const getDateTimeLabel = (appointment: CalendarCardAppointment): string => {
  const appointmentDate = getDateFromAppointment(appointment)
  if (!isValid(appointmentDate)) return ''

  try {
    return formatInTimeZone(appointmentDate, appointment.timeZone, 'EEE, MMM d · h:mm a zzz')
  } catch {
    return ''
  }
}

const sortAppointments = (appointments: CalendarCardAppointment[], isPast: boolean): CalendarCardAppointment[] =>
  [...appointments].sort((firstAppointment, secondAppointment) => {
    if (isPast) {
      const firstIsCanceled = firstAppointment.appointmentStatus === 'canceled'
      const secondIsCanceled = secondAppointment.appointmentStatus === 'canceled'

      if (firstIsCanceled !== secondIsCanceled) return firstIsCanceled ? 1 : -1
    }

    const firstStart = getDateFromAppointment(firstAppointment).getTime()
    const secondStart = getDateFromAppointment(secondAppointment).getTime()

    if (Number.isNaN(firstStart)) return Number.isNaN(secondStart) ? 0 : 1
    if (Number.isNaN(secondStart)) return -1
    return isPast ? secondStart - firstStart : firstStart - secondStart
  })

const buildBaseProgramAwareSessionRow = (
  appointment: CalendarCardAppointment,
  authProviderId: string,
  isPast: boolean,
): BaseProgramAwareSessionRow => {
  const isFacilitatedByAnotherProvider = appointment.lyraProviderId !== authProviderId
  const showActionMenu = !isPast && !isFacilitatedByAnotherProvider && appointment.appointmentStatus !== 'canceled'

  return {
    appointment,
    attendance: getAttendance(appointment, isPast),
    dateTimeLabel: getDateTimeLabel(appointment),
    isFacilitatedByAnotherProvider,
    isPast,
    isTooLateToReschedule: showActionMenu && isWithin24Hours(appointment),
    showActionMenu,
  }
}

export const buildDBTTeensSessionRows = ({
  appointments,
  authProviderId,
  caregiverFirstName,
  clientFirstName,
  isPast,
}: BuildDBTTeensSessionRowsArgs): ProgramAwareSessionRow[] =>
  sortAppointments(appointments, isPast).map((appointment) => {
    const ltSessionType = getLTSessionType(appointment)
    const sessionType = getProgramAwareSessionType(ltSessionType)

    return {
      ...buildBaseProgramAwareSessionRow(appointment, authProviderId, isPast),
      facilitatorName: getFacilitatorName(appointment),
      facilitatorRole: getFacilitatorRole(appointment),
      ltSessionType,
      participantNames: getParticipantNames(
        sessionType,
        appointment.guardianFirstName || caregiverFirstName,
        clientFirstName,
      ),
      sessionType,
    }
  })
